import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { DummyEvent, PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { ObjectHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ObjectHelper";
import { StringHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/StringHelper";
import { $$, IJNativeEvent } from "@seriously-rad/seriouslyrad-framework-core/JNative";
import { PropertyChange } from "@seriously-rad/seriouslyrad-framework-core/PropertyChange";
import { IRadObject, Property, PropertyBoolean, PropertyInt32, RadObject, RadObjectPropertyValues } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { ApplicationInformationService } from "@seriously-rad/seriouslyrad-framework-core/RadWindow";
import { WebApiClientService } from "@seriously-rad/seriouslyrad-framework-core/Server/WebApiClientService";
import { ServiceManager } from "@seriously-rad/seriouslyrad-framework-core/ServiceManager";
import { Toolbar } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Toolbar";
import { ToolbarButton } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ToolbarButton";
import type { INotificationHub } from "./NotificationHub";
import { NotificationHubService } from "./NotificationHubService";
import { UserNotificationPanel } from "./Ui/Controls/UserNotificationPanel";
import { UserNotificationDialog } from "./Ui/Dialogs/UserNotificationDialog";
import { UserNotificationManagerService } from "./UserNotificationManagerService";
import { NAMESPACE } from "./_Namespace";

export interface INotification
{
    createdDate: string;
    message: string;
    targetUrl: string;
    title: string;
}

export type UserNotificationManagerPropertyValues =
    RadObjectPropertyValues
    & {
        alertCount: number;
        notificationSounds: boolean;
    };

export type UserNotificationManagerProperties = {
    [name in keyof UserNotificationManagerPropertyValues]: Property<UserNotificationManagerPropertyValues[name]>
};

export interface IUserNotificationManager extends IRadObject, UserNotificationManagerProperties
{
    dismissNotificationAsync(notificationId: string): Promise<void>;
    newNotification(notification: INotification): void;
}

export class UserNotificationManager extends RadObject implements IUserNotificationManager
{
    // #region Static

    public static override classNamespace = NAMESPACE + "UserNotificationManager";
    public static override className = "UserNotificationManager";

    // #region Constructor

    static {
        $$(document).on("SeriouslyRAD.NotificationHub:RegisterMethods", (evt: IJNativeEvent, hub: INotificationHub) =>
        {
            hub.registerMethod("newUserNotification", (userNotification: INotification) =>
            {
                ServiceManager.getOptional(UserNotificationManagerService)?.newNotification(userNotification);
            });
        });
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<UserNotificationManagerPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                alertCount: 0,
                notificationSounds: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    protected newNotificationAudio?: HTMLAudioElement;
    protected toolbarButtonAlert?: ToolbarButton;
    protected toolbarButtonUserMenuOpen?: ToolbarButton;
    protected userNotificationDialog?: UserNotificationDialog;
    protected userNotificationPanel?: UserNotificationPanel;

    // #endregion

    // #region Properties

    public alertCount!: PropertyInt32;
    public notificationSounds!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<UserNotificationManagerPropertyValues>)
    {
        super(propertyValues);

        this._initialize();
        this._bindEvents();

        // Trigger Property Changes
        this.alertCount.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public async dismissNotificationAsync(notificationId: string): Promise<void>
    {
        await (
            ServiceManager.getRequired(WebApiClientService)
                .createApiMethodRequest(
                    "DataService",
                    "CallDataService",
                    {
                        id: "cc235279-29c2-4a76-b08a-71e574e614ee",
                        operation: "CloseUserNotification",
                        data: ObjectHelper.toJson({ id: notificationId })
                    },
                    null,
                    false
                )
                .executeAsync()
        );

        this._alertCountDecrement();
    }
    public newNotification(notification: INotification): void
    {
        this._alertCountIncrement();
        this.userNotificationPanel?.addNotification(notification);
    }

    // #endregion

    // #region Protected Methods

    protected _alertCountChanged(change: PropertyChange<number>)
    {
        if (this.toolbarButtonAlert)
        {
            this.toolbarButtonAlert.alertCount.set(change.currentValue);
            this.toolbarButtonAlert.showAlert.set(change.currentValue != null && change.currentValue > 0);
        }

        if (this.notificationSounds.get())
            this.newNotificationAudio?.play();
    }
    protected _alertCountDecrement()
    {
        const
            alertCount = this.alertCount.get() ?? 0;

        this.alertCount.set(alertCount - 1);
    }
    protected _alertCountIncrement()
    {
        const
            alertCount = this.alertCount.get() ?? 0;

        this.alertCount.set(alertCount + 1);
    }
    protected _bindEvents()
    {
        $$(document).on("SeriouslyRAD.NotificationHub:Connected", { self: this }, this.onNotificationHubConnected);
        $$(document).on("SeriouslyRAD.NotificationHub:UserSettingsChanged", { self: this }, this.onNotificationHubUserSettingsChanged);

        const
            notificationHub = ServiceManager.getOptional(NotificationHubService);

        if (notificationHub?.connected.get())
            this.onNotificationHubConnected(new DummyEvent(this), notificationHub);
    }
    protected _initialize()
    {
        this.newNotificationAudio = new Audio(StringHelper.format("{0}audio/notification.mp3", ServiceManager.getRequired(ApplicationInformationService).virtualDirectory));
        this.newNotificationAudio.volume = 0.2;

        this.userNotificationDialog = new UserNotificationDialog();
        this.userNotificationPanel = new UserNotificationPanel();

        this.toolbarButtonUserMenuOpen = $$(".rad-action-user-menu-open").radControl();

        if (this.toolbarButtonUserMenuOpen)
        {
            const
                toolbar = this.toolbarButtonUserMenuOpen.parentControlGet();

            Assert.isInstanceOf(Toolbar, toolbar, "toolbar");

            this.toolbarButtonAlert =
                new ToolbarButton({
                    cssClass: "rad-action-user-notification-dialog-open",
                    icon: "alarm"
                }).appendTo(toolbar);

            this.toolbarButtonUserMenuOpen
                .appendTo(toolbar);

            this.toolbarButtonAlert.on("click", { self: this }, this.onToolbarButtonAlertClicked);
        }

        $$(document).triggerHandler("SeriouslyRAD.UserNotificationManager:Initialized");
    }
    protected async _loadUserSettingsAsync()
    {
        const
            data =
                await
                    ServiceManager.getRequired(WebApiClientService)
                        .createEntitySearchRequest(
                            "UserSettings",
                            {
                                filter: "userSettingType/name eq 'Notification Sounds'",
                            }
                        )
                        .executeAsync();

        this.notificationSounds.set(data.length ? data[0].value === "true" : false);
    }
    protected _notificationSoundsChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected async _updateAlertCountAsync()
    {
        const
            result =
                await (
                    ServiceManager.getRequired(WebApiClientService)
                        .createApiMethodRequest(
                            "DataService",
                            "CallDataService",
                            {
                                id: "cc235279-29c2-4a76-b08a-71e574e614ee",
                                operation: "GetUserSummary"
                            },
                            null,
                            false
                        )
                        .executeAsync()
                );

        this.alertCount.set(result?.unread ? result.unread : 0);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: UserNotificationManagerPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.alertCount = new PropertyInt32(this, propertyValues.alertCount, this._alertCountChanged);
        this.notificationSounds = new PropertyBoolean(this, propertyValues.notificationSounds, this._notificationSoundsChanged);

        this.alertCount.overrideSet((value, next) =>
        {
            if (value != null && value < 0)
                value = 0;

            next(value);
        });
    }

    // #endregion

    // #region Event Handlers

    protected onNotificationHubConnected(evt: IJNativeEvent, notificationHub: INotificationHub)
    {
        const
            self = evt.data.self as this;

        self._updateAlertCountAsync();
    }
    protected async onNotificationHubUserSettingsChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        await self._loadUserSettingsAsync();
    }
    protected onToolbarButtonAlertClicked(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        Assert.isNotNullOrUndefined(self.userNotificationDialog, "self.userNotificationDialog");

        self.userNotificationDialog
            .openAsync();
    }

    // #endregion

    // #endregion
}
